"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router";
import { useEditorBundle, useBundleRoot } from "@/hooks/use-editor-bundle";
import { useAuth } from "@/lib/auth";
import { useHeaderAction } from "@/lib/header-action-context";
import { useCapabilityActions } from "@/hooks/use-capability-actions";
import { useCapabilityUpdate } from "@/hooks/use-entity-mutations";
import {
  CapabilityDetailLayout,
  WORKING_COPY_ID,
} from "@/components/capability-detail/CapabilityDetailLayout";
import type { DeferredOverviewDirtyPayload } from "@/components/capability-detail/CapabilityOverviewTab";
import { CapabilityHeaderActions } from "@/components/capability-detail/CapabilityHeaderActions";
import {
  CapabilityPickerDrawer,
  type CapabilityPickerItem,
} from "@/components/agent-config/CapabilityPickerDrawer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FolderOpen, HardDrive, Loader2, Pencil, Plug, X } from "lucide-react";
import { capabilityColors } from "@/lib/capability-colors";
import { api } from "@/lib/api-client";
import { toast } from "sonner";
import { logger } from "@/lib/telemetry";
import { cn } from "@/lib/utils";

const LOG_PREFIX = "workspace-detail-page";
const HEADER_LAYER_ID = "workspace-page";

interface WorkspaceCapabilityData {
  id?: string;
  name?: string;
  description?: string | null;
  tags?: string[];
  type?: string;
  store_type?: string;
  providerRef?: string;
  config?: Record<string, unknown>;
  working_revision?: {
    type_config?: {
      store_type?: string;
      providerRef?: string;
      config?: Record<string, unknown>;
    };
  };
}

function extractWorkspaceState(data: WorkspaceCapabilityData | undefined) {
  if (!data) {
    return { storeType: "", providerRef: "", config: {} as Record<string, unknown> };
  }
  const config = data.config ?? {};
  const manifest = data.working_revision?.type_config ?? {};
  return {
    storeType:
      (manifest.store_type as string | undefined) ??
      (data.store_type as string | undefined) ??
      "",
    providerRef:
      (manifest.providerRef as string | undefined) ??
      (data.providerRef as string | undefined) ??
      "",
    config:
      (manifest.config as Record<string, unknown> | undefined) ??
      (typeof config === "object" && config !== null && !Array.isArray(config)
        ? (config as Record<string, unknown>)
        : {}),
  };
}

function ProviderCardContent({
  providerRef,
  appKey,
  providers,
  bundle,
  onRemove,
  canRemove = true,
}: {
  providerRef: string;
  appKey: string;
  providers: CapabilityPickerItem[];
  bundle: { localRefs?: Record<string, Record<string, unknown>> } | undefined;
  onRemove: () => void;
  canRemove?: boolean;
}) {
  const fromList = providers.find(
    (x) =>
      x.name === providerRef ||
      x.fqcn === providerRef ||
      (x.app_key && `${x.app_key}/${x.name}` === providerRef)
  );
  const refId = providerRef.includes("/")
    ? providerRef
    : appKey
      ? `${appKey}/${providerRef}`
      : null;
  const fromBundle = refId && bundle?.localRefs?.[refId];
  const descFromBundle =
    fromBundle && typeof fromBundle === "object" && !Array.isArray(fromBundle)
      ? (fromBundle as Record<string, unknown>).description
      : undefined;
  const description =
    (fromList?.description?.trim() as string | undefined) ??
    (typeof descFromBundle === "string" ? descFromBundle : null);
  const displayName =
    fromList?.name ??
    (providerRef.includes("/") ? providerRef.split("/").pop() : providerRef);

  return (
    <Card
      className={cn(
        "relative min-h-[88px] border-2 border-l-4 border-slate-200 bg-white p-3 transition-all duration-200 hover:shadow-md"
      )}
      style={{ borderLeftColor: capabilityColors.provider.hex }}
    >
      <div className="flex flex-row items-start gap-3">
        <div
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
            capabilityColors.provider.bgLight,
            capabilityColors.provider.text
          )}
        >
          <Plug size={16} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
            provider
          </span>
          <span className="truncate text-sm font-bold text-slate-900">
            {displayName}
          </span>
          {description && (
            <span className="line-clamp-2 text-[10px] leading-tight text-slate-500">
              {description}
            </span>
          )}
        </div>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-500"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            aria-label="Remove provider"
          >
            <X size={10} />
          </Button>
        )}
      </div>
    </Card>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </p>
      {children}
    </div>
  );
}

export default function WorkspaceDetailPage() {
  const params = useParams<{ appKey: string; name: string }>();
  const appKey = params.appKey;
  const name = params.name;
  const { identity, currentOrganization } = useAuth();
  const { data: bundle, isLoading, refetchWithoutCache } = useEditorBundle(appKey!, name!);
  const data = useBundleRoot(bundle);
  const updateMutation = useCapabilityUpdate(appKey!, name!);
  const { pushHeaderLayer, removeHeaderLayer } = useHeaderAction();
  const status = (data as { status?: string })?.status ?? "";
  const isArchived = status.toLowerCase() === "archived";

  const [providerRef, setProviderRef] = useState("");
  const [providers, setProviders] = useState<CapabilityPickerItem[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [isProviderDrawerOpen, setIsProviderDrawerOpen] = useState(false);
  const [overviewPatch, setOverviewPatch] = useState<Record<string, unknown>>({});
  const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
  const isViewingPublished = selectedRevisionId !== WORKING_COPY_ID;

  const handleOverviewDeferredDirtyChange = useCallback(
    (payload: DeferredOverviewDirtyPayload) => {
      setOverviewPatch((prev) => {
        const next = payload.patch;
        if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
        return next;
      });
    },
    []
  );

  const formReadOnly =
    isArchived ||
    !(data as Record<string, unknown> | undefined)?.working_revision ||
    isViewingPublished;

  const workspaceData = data as WorkspaceCapabilityData | undefined;

  const immutableRevisions = useMemo(
    () =>
      ((data as Record<string, unknown>)?.immutable_revisions ?? []) as Array<{
        id: string;
        type_config?: Record<string, unknown>;
      }>,
    [data]
  );

  const selectedImmutableRevision = useMemo(
    () => immutableRevisions.find((r) => r.id === selectedRevisionId),
    [immutableRevisions, selectedRevisionId]
  );

  useEffect(() => {
    setLoadingProviders(true);
    api
      .get("/capabilities", { params: { type: "tool" } })
      .then((res) => {
        const payload = res.data;
        const items = (
          Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
              ? payload.data
              : []
        ) as CapabilityPickerItem[];
        setProviders(
          items.map((p) => ({
            ...p,
            fqcn: p.fqcn ?? (p.app_key && p.name ? `${p.app_key}/${p.name}` : p.name),
          }))
        );
      })
      .catch(() => setProviders([]))
      .finally(() => setLoadingProviders(false));
  }, []);

  useEffect(() => {
    if (isViewingPublished && selectedImmutableRevision) {
      const tc = selectedImmutableRevision.type_config ?? {};
      setProviderRef((tc.providerRef as string) ?? "");
      return;
    }
    setProviderRef(extractWorkspaceState(workspaceData).providerRef);
  }, [
    workspaceData?.config,
    workspaceData?.providerRef,
    workspaceData?.working_revision?.type_config,
    isViewingPublished,
    selectedImmutableRevision,
  ]);

  const extractedSource = useMemo(
    () => extractWorkspaceState(workspaceData),
    [
      workspaceData?.config,
      workspaceData?.providerRef,
      workspaceData?.store_type,
      workspaceData?.working_revision?.type_config,
    ]
  );
  const isDirty = providerRef !== extractedSource.providerRef;
  const overviewDirty = Object.keys(overviewPatch).length > 0;
  const anyDirty = isDirty || overviewDirty;

  const handleSave = useCallback(() => {
    if (!appKey || !name) return;
    if (!isDirty && !overviewDirty) return;
    const payload: Record<string, unknown> = {};
    if (overviewDirty) {
      Object.assign(payload, overviewPatch);
    }
    if (isDirty) {
      payload.type_config = {
        providerRef: providerRef || null,
      };
    }
    updateMutation.mutate(payload, {
      onSuccess: () => {
        setOverviewPatch({});
        toast.success("Workspace updated");
        logger.info(`${LOG_PREFIX} handleSave: workspace updated`, {
          providerRef,
        });
      },
      onError: (err) => {
        logger.error(`${LOG_PREFIX} handleSave: update failed`, { error: err });
        toast.error("Failed to update workspace");
      },
    });
  }, [
    appKey,
    name,
    isDirty,
    overviewDirty,
    overviewPatch,
    providerRef,
    updateMutation,
  ]);

  const actions = useCapabilityActions({
    appKey: appKey!,
    name: name!,
    typeLabel: "Workspace",
    data: data as Record<string, unknown> | null,
    refetch: refetchWithoutCache,
    customSave: handleSave,
    customIsDirty: anyDirty,
    customIsSaving: updateMutation.isPending,
  });

  const saveRef = useRef(actions.save);
  saveRef.current = actions.save;
  const publishRef = useRef(actions.publish);
  publishRef.current = actions.publish;
  const editRef = useRef(actions.edit);
  editRef.current = actions.edit;

  useEffect(() => {
    if (!data || isArchived) {
      removeHeaderLayer(HEADER_LAYER_ID);
      return;
    }
    pushHeaderLayer(HEADER_LAYER_ID, {
      action: (
        <CapabilityHeaderActions
          typeLabel="Workspace"
          hasWorkingRevision={actions.hasWorkingRevision}
          isViewingPublished={actions.isViewingPublished}
          isArchived={isArchived}
          selectedPublishedLabel={actions.selectedPublishedLabel}
          isDirty={actions.isDirty}
          isSaving={actions.isSaving}
          isPublishing={actions.isPublishing}
          onSave={() => void saveRef.current()}
          onPublish={() => void publishRef.current()}
          onEdit={() => editRef.current()}
          onArchive={() => actions.openArchiveDialog()}
          onSettings={() => actions.openSettings()}
        />
      ),
    });
    return () => removeHeaderLayer(HEADER_LAYER_ID);
  }, [
    data,
    isArchived,
    actions.hasWorkingRevision,
    actions.isViewingPublished,
    actions.selectedPublishedLabel,
    actions.isDirty,
    actions.isSaving,
    actions.isPublishing,
    pushHeaderLayer,
    removeHeaderLayer,
  ]);

  const displayConfig = useMemo(() => {
    if (isViewingPublished && selectedImmutableRevision) {
      const tc = selectedImmutableRevision.type_config ?? {};
      return {
        storeType: (tc.store_type as string | undefined) ?? "",
        config: (tc.config as Record<string, unknown> | undefined) ?? {},
      };
    }
    return {
      storeType: extractedSource.storeType,
      config: extractedSource.config,
    };
  }, [extractedSource, isViewingPublished, selectedImmutableRevision]);

  const configEntries = Object.entries(displayConfig.config);
  const colors = capabilityColors.workspace;

  const providerSlot = (
    <div
      id="workspace-provider"
      role="button"
      tabIndex={formReadOnly ? -1 : 0}
      onClick={() => {
        if (!formReadOnly) setIsProviderDrawerOpen(true);
      }}
      onKeyDown={(e) => {
        if (formReadOnly) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsProviderDrawerOpen(true);
        }
      }}
      className={cn(!formReadOnly && "cursor-pointer")}
    >
      {providerRef ? (
        <ProviderCardContent
          providerRef={providerRef}
          appKey={appKey ?? ""}
          providers={providers}
          bundle={bundle}
          canRemove={!formReadOnly}
          onRemove={() => setProviderRef("")}
        />
      ) : (
        <Card
          className={cn(
            "border-2 border-dashed border-slate-200 bg-slate-50/50 p-3",
            formReadOnly && "pointer-events-none opacity-60"
          )}
        >
          <div className="flex flex-col items-center justify-center py-2">
            <span className="mb-1 text-green-400">
              <Plug size={24} />
            </span>
            <p className="text-xs font-medium text-green-600">
              {formReadOnly ? "No provider" : "Click to select provider"}
            </p>
            {loadingProviders && !formReadOnly && (
              <p className="mt-1 text-xs text-slate-400">Loading capabilities...</p>
            )}
            <p className="mt-2 text-xs italic text-slate-500">
              The capability that handles I/O for this workspace (e.g. a filesystem tool).
            </p>
          </div>
        </Card>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <p className="text-slate-600">Workspace not found.</p>
      </div>
    );
  }

  return (
    <>
      <CapabilityDetailLayout
        typeLabel="Workspace"
        appKey={appKey}
        capabilityName={name}
        selectedRevisionId={selectedRevisionId}
        onRevisionChange={setSelectedRevisionId}
        onOverviewDeferredDirtyChange={handleOverviewDeferredDirtyChange}
        pendingOverviewPatch={overviewPatch}
        onSaveDeferred={() => void handleSave()}
        isOverviewDirty={anyDirty}
        isSavingOverview={updateMutation.isPending}
        overviewFormDisabled={
          updateMutation.isPending || !actions.hasWorkingRevision || isViewingPublished
        }
      >
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <FolderOpen size={16} className={colors.text} />
                Workspace
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Store type">
                {displayConfig.storeType ? (
                  <div className="flex items-center gap-2">
                    <HardDrive size={14} className="text-slate-500" />
                    <code className="rounded bg-slate-100 px-2 py-0.5 font-mono text-sm text-slate-700">
                      {displayConfig.storeType}
                    </code>
                  </div>
                ) : (
                  <p className="text-sm italic text-slate-500">Not set</p>
                )}
              </Field>
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Provider
                </p>
                {providerSlot}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Config</CardTitle>
            </CardHeader>
            <CardContent>
              {configEntries.length > 0 ? (
                <div className="space-y-3">
                  {configEntries.map(([key, value]) => (
                    <Field key={key} label={key}>
                      <code className="block whitespace-pre-wrap break-all rounded bg-slate-100 px-2 py-1.5 font-mono text-sm text-slate-700">
                        {typeof value === "string"
                          ? value
                          : JSON.stringify(value, null, 2)}
                      </code>
                    </Field>
                  ))}
                </div>
              ) : (
                <p className="text-sm italic text-slate-500">
                  No store configuration defined.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </CapabilityDetailLayout>

      <AlertDialog
        open={actions.replaceDialogOpen}
        onOpenChange={actions.setReplaceDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Pencil size={18} className="text-amber-600" />
              Replace working copy?
            </AlertDialogTitle>
            <AlertDialogDescription>
              A working copy already exists. Replacing it will discard any unsaved changes
              and create a new working copy based on the selected published version.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={actions.handleConfirmReplace}>
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CapabilityPickerDrawer
        isOpen={isProviderDrawerOpen && !formReadOnly}
        onClose={() => setIsProviderDrawerOpen(false)}
        slotType="tool"
        items={providers}
        selectedRefs={providerRef ? [providerRef] : []}
        onSelect={(selected) => setProviderRef(selected)}
        loading={loadingProviders}
        appKey={appKey}
        currentUserId={identity?.sub}
        currentOrgId={currentOrganization?.id}
      />
    </>
  );
}
